import { useMutation, useQueryClient } from '@tanstack/react-query';

import { splitTask } from '@/lib/api/generated/api';
import { workspaceKeys } from '@/features/workspaces';
import { TASK_MUTATION_KEYS } from './taskMutationKeys';
import { classifySplitFailure } from '../errors';
import { taskKeys } from '../queryKeys';

export interface SplitTaskInput {
  workspaceId: string;
  parentId: string;
  title: string;
  childTaskIds: string[];
}

/**
 * Move some of a waiting parent's subtasks into a new task (spec 137 US3,
 * service spec 119).
 *
 * The response carries the new task, but the parent's progress, its remaining
 * subtasks and the new task's place on the board all change at once, so the
 * workspace aggregate is re-read rather than patched. The prefix reaches both
 * the plain and the include-hidden aggregate.
 */
export function useSplitTask() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: TASK_MUTATION_KEYS.split,
    mutationFn: async ({
      workspaceId,
      parentId,
      title,
      childTaskIds,
    }: SplitTaskInput) => {
      const response = await splitTask(workspaceId, parentId, {
        title: title.trim(),
        child_task_ids: childTaskIds,
      });
      if (response.status === 201) return response.data;
      throw classifySplitFailure(response.status, response.data);
    },
    onSuccess: (_result, { workspaceId }) => {
      // A split is logged on both runs (spec 148).
      void queryClient.invalidateQueries(
        taskKeys.activityInWorkspace(workspaceId),
      );
      return queryClient.invalidateQueries({
        queryKey: workspaceKeys.detail(workspaceId),
      });
    },
  });
}
